import { createContext, useReducer, ReactNode, useCallback } from 'react';
import AppReducer from './AppReducer';
import { DeliveryStatusType, UserDeliveryRequirements, AppAction, AppState } from './contextTypes';

const initialState: AppState = {
  partner: {
    name: '',
    countryCode: 'se'
  },
  settings: {
    customCSS: '',
    brand: {
      color: '#6F7DFF',
      favicon: '',
      riderIcon: '',
      userIcon: '',
      feedbackColor: '',
      feedbackActiveColour: ''
    },
    floorName: {
      ground_floor: 'GF',
      basement: 'B',
      sub_basement: 'SB'
    },
    feedbackSuggestions: [],
    locales: {
      en: {},
      sv: {}
    },
    displayDeliveryConfirmationPhotos: false,
    trackingUrl: 'https://consumer.velove.se',
    enableCustomerSupportForSuccessfulDeliveries: false,
    displayName: '',
    deliveryVerificationMethods: null,
    veloveT1Sorting: false
  },
  trackingDetails: {
    recipient: {
      floorNo: null,
      intercom: '',
      doorCode: '',
      status: DeliveryStatusType.PENDING,
      entryOption: null
    },
    streetAddress: {
      latitude: 0,
      longitude: 0
    },
    task: {
      completedAt: null,
      completeBefore: null,
      completeAfter: null,
      status: undefined,
      taskInstructions: '',
      consumerUpdatedInfoAt: null,
      id: '',
      nameOnDoor: '',
      verificationCode: null,
      identificationCheckRequired: null,
      recipientMinimumAge: null,
      recipientMustMatchEndCustomer: null,
      requireSignature: null,
      preOrderRequired: null,
      trackingLinkOpenedAt: null,
      failureReason: null
    },
    package: {
      internalPackageId: '',
      externalPackageId: '',
      userDeliveryRequirements: UserDeliveryRequirements.CONTACTLESS,
      originalCompleteBefore: null,
      originalCompleteAfter: null,
      deliveryNotificationType: null
    },
    route: {
      id: '',
      status: DeliveryStatusType.PENDING,
      shiftId: null
    },
    eta: {
      lowerBound: null,
      upperBound: null,
      predictedArrival: null,
      originalPredictedArrival: null
    },
    lastKnownLatitude: 0,
    lastKnownLongitude: 0,
    bearing: 0,
    stopsAhead: 0,
    feedbackId: null,
    rider: {
      name: null
    },
    hubName: null,
    verifiedPhotoUrls: [],
    trackingEvents: [],
    announcement: null,
    deliveryWindows: []
  },
  isCustomerInputsOpen: false,
  bankId: {
    orderRef: null
  },
  isPolling: false,
  setPartnerAndSettings: (data: any) => {},
  setTrackingDetails: (data: any) => {},
  setCustomerInputsOpen: () => {},
  setBankIdOderRef: (data: any) => {},
  setPollingStatus: () => {}
};

export const AppContext = createContext(initialState);

export const AppContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(AppReducer, initialState);

  const setPartnerAndSettings = useCallback((data: any) => {
    dispatch({ type: AppAction.SET_PARTNER_AND_SETTINGS, payload: data });
  }, []);

  const setTrackingDetails = useCallback((data: any) => {
    dispatch({ type: AppAction.SET_TRACKING_DETAILS, payload: data });
  }, []);

  const setCustomerInputsOpen = useCallback(() => {
    dispatch({ type: AppAction.SET_CUSTOMER_INPUTS_OPEN, payload: null });
  }, []);

  const setBankIdOderRef = useCallback((data: any) => {
    dispatch({ type: AppAction.SET_BANKID_ORDER_REF, payload: data });
  }, []);

  const setPollingStatus = useCallback(() => {
    dispatch({ type: AppAction.SET_POLLING_STATUS, payload: null });
  }, []);

  return (
    <AppContext.Provider
      value={{
        ...state,
        setPartnerAndSettings,
        setTrackingDetails,
        setCustomerInputsOpen,
        setBankIdOderRef,
        setPollingStatus
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
